
import React, { useState } from 'react';
import { AppRoute } from '../types';

interface NavbarProps {
  onNavigate: (route: AppRoute) => void;
  activeRoute: AppRoute;
}

const Navbar: React.FC<NavbarProps> = ({ onNavigate, activeRoute }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { label: 'Printer owner', route: AppRoute.PRINTER_OWNER },
    { label: 'About', route: AppRoute.ABOUT },
    { label: 'For makers', route: AppRoute.FOR_MAKERS },
  ];

  const handleNavigate = (route: AppRoute) => {
    onNavigate(route);
    setIsMobileMenuOpen(false);
    document.body.style.overflow = 'auto';
  };

  const toggleMobileMenu = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    document.body.style.overflow = newState ? 'hidden' : 'auto';
  };

  return (
    <nav className="fixed top-0 w-full z-[100] border-b border-border bg-background/90 backdrop-blur-md transition-all duration-300">
      <div className="max-w-[1400px] mx-auto px-6 h-16 md:h-20 flex items-center justify-between">
        <div 
          className="flex items-center cursor-pointer group" 
          onClick={() => handleNavigate(AppRoute.HOME)}
        >
          <span className="font-display font-bold tracking-[0.25em] text-xl uppercase">Zetra</span>
        </div>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-10 text-[11px] uppercase tracking-widest font-bold text-neutral-500">
          {navLinks.map((link) => (
            <button 
              key={link.route}
              onClick={() => handleNavigate(link.route)}
              className={`hover:text-primary transition-all relative py-2 ${
                activeRoute === link.route ? 'text-primary after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:bg-primary after:rounded-full' : ''
              }`}
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <button 
            onClick={() => {
              if (activeRoute === AppRoute.HOME) {
                document.getElementById('waitlist-section')?.scrollIntoView({ behavior: 'smooth' });
              } else {
                handleNavigate(AppRoute.HOME);
                setTimeout(() => document.getElementById('waitlist-section')?.scrollIntoView({ behavior: 'smooth' }), 100);
              }
            }}
            className="hidden sm:block bg-primary text-white text-[11px] font-bold uppercase tracking-widest px-8 py-3 rounded-full hover:bg-neutral-800 transition-all active:scale-95 shadow-md shadow-black/5"
          >
            Join waitlist
          </button>
          
          {/* Mobile Menu Toggle */}
          <button 
            className="lg:hidden w-12 h-12 flex items-center justify-center text-primary rounded-full hover:bg-neutral-100 transition-colors"
            onClick={toggleMobileMenu}
            aria-label="Toggle Menu"
          >
            <iconify-icon icon={isMobileMenuOpen ? "lucide:x" : "lucide:menu"} width="28"></iconify-icon>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[64px] md:top-[80px] bg-background z-[90] animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="p-10 flex flex-col gap-10 text-3xl font-display uppercase tracking-tight overflow-y-auto h-full">
            {navLinks.map((link) => (
              <button 
                key={link.route}
                onClick={() => handleNavigate(link.route)}
                className={`text-left transition-colors ${activeRoute === link.route ? 'text-primary' : 'text-neutral-300 hover:text-neutral-500'}`}
              >
                {link.label}
              </button>
            ))}
            <div className="mt-auto pt-10 border-t border-border flex flex-col gap-6">
               <button 
                className="w-full bg-primary text-white py-6 rounded-3xl text-sm font-bold uppercase tracking-widest shadow-xl"
                onClick={() => {
                  handleNavigate(AppRoute.HOME);
                  setTimeout(() => document.getElementById('waitlist-section')?.scrollIntoView({ behavior: 'smooth' }), 100);
                }}
              >
                Join waitlist
              </button>
              <div className="flex justify-between text-[11px] font-mono text-neutral-400 uppercase tracking-widest">
                <span>ZT v1.0.4</span>
                <span>©2024</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
